import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import type { IBetSlipCashOut, SupportedCashOutConditions } from '@/interface/IBetSlipCashOut';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

function isCashOutSupported(bet: IBetSlipCashOut, conditions: SupportedCashOutConditions) {
    if (!bet.leagueId || !bet.isMainMarket) return false;

    // The conditions come from a JSON toggle without schema validation, so guard the shape at runtime.
    const { marketTypes, leagueIds } = conditions[bet.sportType] ?? {};
    if (!Array.isArray(marketTypes) || !marketTypes.includes(bet.marketType)) return false;

    return leagueIds === '*' || (Array.isArray(leagueIds) && leagueIds.includes(bet.leagueId));
}

export function useSingleBetCashOut(bet: Ref<IBetSlipCashOut>) {
    const {
        supportedCashOutConditions,
        isCashOutEnabled,
        isCashOutPageEnabled,
    } = storeToRefs(useFeatureToggleStore());
    const { isCashOutTooltipShowOnce, hasSeenCashOutTutorial } = storeToRefs(useRootStore());

    const isDisplayCashOutIcon = computed(() => (
        isCashOutEnabled.value && isCashOutSupported(bet.value, supportedCashOutConditions.value)
    ));

    const isDisplayCashOutTooltip = computed(() => isDisplayCashOutIcon.value
        && isCashOutPageEnabled.value
        && !isCashOutTooltipShowOnce.value
        && !hasSeenCashOutTutorial.value);

    return {
        isDisplayCashOutIcon,
        isDisplayCashOutTooltip,
    };
}

export function useParlayCashOut(subBets: Ref<IBetSlipCashOut[]>) {
    const { supportedCashOutConditions, isMpCashOutEnabled } = storeToRefs(useFeatureToggleStore());

    const isDisplayCashOutIcon = computed(() => (
        isMpCashOutEnabled.value
        && subBets.value.length > 0
        && subBets.value.every(subBet => isCashOutSupported(subBet, supportedCashOutConditions.value))
    ));

    return { isDisplayCashOutIcon };
}
